import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import type { CampaignWorkflow } from '@/types/campaign';
import { fetchCampaigns } from '@/utils/campaigns/fetchCampaigns';
import { fetchWithRewrites } from '@/utils/fetchWithRewrites';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import IncomingTab from '../tabs/Incoming';
import OutgoingTab from '../tabs/Outgoing';

interface MarketingCampaignDetailsProps {
  campaignId: string;
}

function MarketingCampaignDetails({
  campaignId,
}: MarketingCampaignDetailsProps): React.ReactNode {
  const [tabValue, setTabValue] = useState(0);
  const [campaign, setCampaign] = useState<CampaignWorkflow | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const theme = useAlchemystStoreForAi((state) => state.theme) as
    | 'dark'
    | 'light';

  // Get campaign data from store
  const campaignWorkflows = useAlchemystStoreForAi(
    (store) => store.campaignWorkflows
  );
  const setAlchemystStoreState = useAlchemystStoreForAi(
    (store) => store.setStoreState
  );

  // Colors based on the platform's theme with more muted, grayish tones
  const colors = {
    tabActive: theme === 'dark' ? '#444444' : '#eaeaea',
    tabActiveText: theme === 'dark' ? '#ffffff' : '#333333',
    tabInactive: theme === 'dark' ? '#2a2a2a' : '#f5f5f5',
    tabInactiveText: theme === 'dark' ? '#a0a0a0' : '#666666',
    background: theme === 'dark' ? '#18181b' : '#ffffff',
    border: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
  };

  // Fetch campaign details
  useEffect(() => {
    const fetchCampaignDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        // For newly created campaigns, check if it exists in the store first
        const foundCampaignInStore = campaignWorkflows.find(
          (c) => c.id === campaignId
        );

        if (foundCampaignInStore) {
          setCampaign(foundCampaignInStore);
          setAlchemystStoreState({
            selectedLeads: foundCampaignInStore.leads ?? [],
          });
        } else {
          // Fetch from API
          const campaignWorkflows = await fetchCampaigns('ron');
          const foundCampaign = campaignWorkflows.find(
            (campaign) => campaign.id === campaignId
          );

          if (foundCampaign) {
            setCampaign(foundCampaign);
            setAlchemystStoreState({
              selectedLeads: foundCampaign.leads ?? [],
            });
          } else {
            // Try to fetch directly if not found in list
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);

            try {
              const response = await fetchWithRewrites(
                `/api/v1/campaigns/fetch/${campaignId}`,
                { signal: controller.signal }
              );

              if (!response.ok) {
                throw new Error(`Campaign not found: ${response.statusText}`);
              }

              const data = await response.json();
              console.log('TEH DATA', data);
              setCampaign(data);
              setAlchemystStoreState({
                selectedLeads: data.leads ?? [],
              });
            } finally {
              clearTimeout(timeoutId);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching campaign:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load campaign'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCampaignDetails();
  }, [campaignId, campaignWorkflows, setAlchemystStoreState]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !campaign) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <Typography color="error" variant="h6">
          {error || 'Campaign not found'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid',
          borderColor: colors.border,
          borderRadius: 2,
          overflow: 'hidden',
          height: '100%',
          background: colors.background,
        }}
      >
        {/* Tabs */}
        <Box sx={{ display: 'flex' }}>
          <Box
            sx={{
              flex: 1,
              p: 2,
              bgcolor: tabValue === 0 ? colors.tabActive : colors.tabInactive,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              transition: 'background-color 0.3s ease',
            }}
            onClick={() => setTabValue(0)}
          >
            <Typography
              variant="h6"
              fontWeight={tabValue === 0 ? 'bold' : 'normal'}
              color={
                tabValue === 0 ? colors.tabActiveText : colors.tabInactiveText
              }
            >
              Outgoing
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              p: 2,
              bgcolor: tabValue === 1 ? colors.tabActive : colors.tabInactive,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              transition: 'background-color 0.3s ease',
            }}
            onClick={() => setTabValue(1)}
          >
            <Typography
              variant="h6"
              fontWeight={tabValue === 1 ? 'bold' : 'normal'}
              color={
                tabValue === 1 ? colors.tabActiveText : colors.tabInactiveText
              }
            >
              Incoming
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {tabValue === 0 ? (
            <OutgoingTab theme={theme} campaign={campaign} />
          ) : (
            <IncomingTab theme={theme} />
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default MarketingCampaignDetails;
